import { useMemo } from "react";
import dayjs from "dayjs";

import { useGetOnSite } from "../../../api/insights";
import {
  DailyAreaChart,
  RankedBarChart,
  ResponsesPieChart,
  formatDay,
} from "../charts";
import SectionShell, {
  ChartPair,
  Stat,
  formatNum,
  formatPct,
} from "../SectionShell";
import { useChartTimeFrame } from "../useChartTimeFrame";
import { sliceByDay } from "../timeFrame";

const DAY_PALETTE = [
  "blue.6",
  "teal.6",
  "grape.6",
  "orange.6",
  "lime.6",
  "pink.6",
  "cyan.6",
  "indigo.6",
];

const formatHourWindow = (start) => {
  if (start == null) return "—";
  const from = dayjs(start);
  return `${from.format("MMM D, HH:00")}–${from.add(1, "hour").format("HH:00")}`;
};

function QrCheckIn({ eventId }) {
  const { data, isPending, isError, error } = useGetOnSite(eventId);
  const tf = useChartTimeFrame(eventId);

  const perHour = useMemo(
    () =>
      sliceByDay(data?.checkInsPerHour, tf.resolved, (r) =>
        dayjs(r.hour).format("YYYY-MM-DD")
      ),
    [data, tf.resolved]
  );

  // The API keys each day series by ISO date; format to a readable label for
  // both the series legend and the matching row values. Days outside the
  // selected range are dropped.
  const perDay = useMemo(() => {
    const allDays = data?.checkInsPerHourByDay?.daysList ?? [];
    const daysList = sliceByDay(allDays, tf.resolved, (d) => d);
    const rows = data?.checkInsPerHourByDay?.days ?? [];
    const series = daysList.map((day, i) => ({
      name: formatDay(day),
      color: DAY_PALETTE[i % DAY_PALETTE.length],
    }));
    const mapped = rows.map((row) => {
      const out = { day: row.day };
      for (const day of daysList) out[formatDay(day)] = row[day] ?? 0;
      return out;
    });
    return { series, rows: mapped };
  }, [data, tf.resolved]);

  return (
    <SectionShell
      id="qrCheckIn"
      title="QR check-in"
      description="On-site check-in efficiency and arrival patterns."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="qrCheckIn"
      stats={
        <>
          <Stat
            part="totalCheckedIn"
            label="Total checked in"
            value={formatNum(data?.totalCheckedIn)}
          />
          <Stat
            part="peakCheckInWindow"
            label="Peak check-in window"
            value={formatHourWindow(data?.peakWindow?.start)}
            hint={
              data?.peakWindow
                ? `${formatNum(data.peakWindow.count)} check-ins`
                : undefined
            }
          />
          <Stat
            part="checkedInFirstTwoHours"
            label="Checked in within first 2h"
            value={formatPct(data?.pctFirstTwoHours ?? undefined)}
          />
          <Stat
            part="lateArrivals"
            label="Late arrivals"
            value={formatPct(data?.pctLateArrivals ?? undefined)}
            hint="After the first session start"
          />
        </>
      }
      lead={
        <DailyAreaChart
          part="checkInsPerHour"
          title="QR check-ins per hour"
          data={perHour}
          series={[{ name: "Check-ins", color: "teal.6" }]}
          bucketSelector
          defaultBucket="1"
          headerRight={tf.control}
        />
      }
    >
      <ChartPair>
        <ResponsesPieChart
          part="profileBreakdown"
          title="Attendee profile breakdown"
          data={data?.profileBreakdown ?? []}
        />
        <RankedBarChart
          part="ticketTypeAttendance"
          title="Attendance by ticket type"
          data={data?.ticketTypeAttendance ?? []}
          categoryKey="type"
          series={[{ name: "Checked in", color: "blue.6" }]}
        />
      </ChartPair>

      <DailyAreaChart
        part="checkInsPerHourByDay"
        title="Check-in per hour, per day"
        data={perDay.rows}
        series={perDay.series}
        curveType="linear"
        withFill={false}
        withPrintPointLabels={false}
      />
    </SectionShell>
  );
}

export default QrCheckIn;
